import { useState } from 'react';
import { Download, Trash2, AlertTriangle, FileText, FileJson, CheckCircle } from 'lucide-react';
import { useUser } from '@hooks/useUser';
import { useAuth } from '@hooks/useAuth';
import { exportService } from '@services/exportService';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import Modal from '@components/common/Modal';
import toast from 'react-hot-toast';

const Data = () => {
  const { deleteAccount, isDeletingAccount } = useUser();
  const { logout } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      await (format === 'csv' ? exportService.exportCSV() : exportService.exportJSON());
      toast.success('Exportação concluída! O download iniciou automaticamente.');
    } catch {
      toast.error('Erro ao exportar dados. Tente novamente.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDelete = async () => {
    await deleteAccount(deletePassword);
    logout();
  };

  const canConfirmDelete = deleteConfirmText === 'EXCLUIR' && deletePassword.length >= 1;

  return (
    <div className="space-y-6">
      {/* Exportar dados */}
      <Card title="Exportar Dados" subtitle="Baixe uma cópia de todas as suas informações">
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Exporte transações, contas, categorias e configurações em diferentes formatos.
          </p>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                  <FileText size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">CSV</p>
                  <p className="text-xs text-gray-500">Planilha Excel/Sheets</p>
                </div>
              </div>
              <Button
                fullWidth
                variant="secondary"
                size="sm"
                leftIcon={<Download size={15} />}
                onClick={() => handleExport('csv')}
                isLoading={isExporting}
              >
                Exportar CSV
              </Button>
            </div>

            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                  <FileJson size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">JSON</p>
                  <p className="text-xs text-gray-500">Backup completo</p>
                </div>
              </div>
              <Button
                fullWidth
                variant="secondary"
                size="sm"
                leftIcon={<Download size={15} />}
                onClick={() => handleExport('json')}
                isLoading={isExporting}
              >
                Exportar JSON
              </Button>
            </div>
          </div>

          <p className="text-xs text-gray-400">
            A exportação inclui os últimos 2 anos de dados. Arquivos grandes podem demorar alguns segundos.
          </p>
        </div>
      </Card>

      {/* Zona de perigo */}
      <Card className="border-red-200 dark:border-red-900/50">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Zona de Perigo</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Ações irreversíveis que afetam permanentemente sua conta.
            </p>
          </div>
        </div>

        <div className="p-4 border border-red-200 dark:border-red-800 rounded-xl bg-red-50 dark:bg-red-900/10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-300">Excluir conta permanentemente</p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                Remove todos os dados, transações, contas e configurações. Esta ação não pode ser desfeita.
              </p>
            </div>
            <Button
              variant="danger"
              size="sm"
              leftIcon={<Trash2 size={15} />}
              onClick={() => setShowDeleteModal(true)}
              className="flex-shrink-0"
            >
              Excluir Conta
            </Button>
          </div>
        </div>
      </Card>

      {/* Modal de exclusão */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeletePassword(''); setDeleteConfirmText(''); }}
        title="Excluir Conta Permanentemente"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => { setShowDeleteModal(false); setDeletePassword(''); setDeleteConfirmText(''); }}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              isLoading={isDeletingAccount}
              disabled={!canConfirmDelete}
              leftIcon={<Trash2 size={16} />}
            >
              Excluir Permanentemente
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-300 font-medium mb-1">
              Esta ação é irreversível!
            </p>
            <p className="text-xs text-red-600 dark:text-red-400">
              Todos os seus dados serão permanentemente excluídos: transações, contas, categorias, investimentos, simulações e configurações.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Digite <strong>EXCLUIR</strong> para confirmar
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder="EXCLUIR"
              className="block w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
            />
          </div>

          <Input
            label="Confirme sua senha"
            type="password"
            value={deletePassword}
            onChange={e => setDeletePassword(e.target.value)}
            placeholder="Sua senha atual"
          />
        </div>
      </Modal>
    </div>
  );
};

export default Data;
